import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import moment from "moment";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import Sidebar from "../common/Sidebar";
import Button from "../common/Button";
import logoIcon from "../../assets/common/logo-icon.svg";
import logout from "../../assets/common/logout.svg";
import finish from "../../assets/agent/End Day Icon.svg";
import { useAgentInsights } from "./mobile/useAgentInsights";
import MobileHomeScreen from "./mobile/MobileHomeScreen";
import MoodFace from "./mobile/MoodFace";

const MOOD_OPTIONS = [
  { value: 1, color: "#e34948", labelKey: "moodVeryBad" },
  { value: 2, color: "#eb6834", labelKey: "moodBad" },
  { value: 3, color: "#eda100", labelKey: "moodNeutral" },
  { value: 4, color: "#639922", labelKey: "moodGood" },
  { value: 5, color: "#1baf7a", labelKey: "moodVeryGood" },
];

const CONFETTI_COLORS = ["#136BF0", "#FF9900", "#1baf7a", "#e34948", "#eda100"];

const Confetti = ({ phrase }) => {
  const pieces = React.useMemo(
    () =>
      Array.from({ length: 60 }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
        duration: 1.6 + Math.random() * 1.2,
        delay: Math.random() * 0.25,
        width: 5 + Math.random() * 5,
        height: 8 + Math.random() * 6,
        rotate: Math.random() * 360,
      })),
    []
  );

  return createPortal(
    <div className="fixed inset-0 pointer-events-none overflow-hidden flex items-center justify-center" style={{ zIndex: 9999 }}>
      <style>{`
        @keyframes confetti-fall {
          0% { transform: translateY(-10vh) rotate(0deg); opacity: 1; }
          100% { transform: translateY(110vh) rotate(720deg); opacity: 0; }
        }
        @keyframes confetti-phrase {
          0% { transform: scale(0.6); opacity: 0; }
          15% { transform: scale(1); opacity: 1; }
          75% { transform: scale(1); opacity: 1; }
          100% { transform: scale(1.1); opacity: 0; }
        }
      `}</style>
      {pieces.map((p) => (
        <span
          key={p.id}
          className="absolute top-0 rounded-sm"
          style={{
            left: `${p.left}%`,
            width: p.width,
            height: p.height,
            backgroundColor: p.color,
            animation: `confetti-fall ${p.duration}s ease-in ${p.delay}s forwards`,
            transform: `rotate(${p.rotate}deg)`,
          }}
        />
      ))}
      {phrase && (
        <span
          className="text-3xl md:text-5xl font-bold text-white text-center px-6"
          style={{
            textShadow: "0 2px 12px rgba(0,0,0,0.35)",
            animation: "confetti-phrase 2.3s ease-out forwards",
          }}
        >
          {phrase}
        </span>
      )}
    </div>,
    document.body
  );
};

const EmpathyOverlay = ({ phrase }) => {
  return createPortal(
    <div
      className="fixed inset-0 pointer-events-none flex items-center justify-center px-6"
      style={{ zIndex: 9999 }}
    >
      <style>{`
        @keyframes empathy-fade {
          0% { transform: scale(0.9); opacity: 0; }
          15% { transform: scale(1); opacity: 1; }
          80% { transform: scale(1); opacity: 1; }
          100% { transform: scale(1.02); opacity: 0; }
        }
      `}</style>
      <div
        className="bg-white rounded-2xl shadow-2xl px-8 py-6 max-w-md text-center border border-blue-100"
        style={{ animation: "empathy-fade 2.5s ease-out forwards" }}
      >
        <p className="text-xl md:text-2xl font-semibold text-[#136BF0]">{phrase}</p>
      </div>
    </div>,
    document.body
  );
};

const AgentDashboard = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const user = sessionStorage.getItem("userName");

  const [sidebarPinned, setSidebarPinned] = useState(() => {
    const saved = localStorage.getItem("agentSidebarPinned");
    return saved === "true";
  });

  const {
    period,
    moodStatus,
    moodSubmitting,
    celebrate,
    showEmpathy,
    getHighestPriorityCallType,
    handleMonthChange,
    handleMoodClick,
    kpisByDay,
    meetingsByDay,
    answeredPct,
    notAnsweredPct,
    ranking,
    rankBarPct,
  } = useAgentInsights();

  const handleStartCalls = () => {
    const callType = getHighestPriorityCallType();
    if (callType) {
      navigate(`/agent/workspace/${callType}`);
    }
  };

  return (
    <>
    <div className="hidden lg:flex min-h-screen bg-secondaryBg">
      {celebrate && <Confetti phrase={t("agent.agentDashboard.insights.celebratePhrase")} />}
      {showEmpathy && <EmpathyOverlay phrase={t("agent.agentDashboard.insights.moodDoneEmpathy")} />}
      <Sidebar
        logoIcon={logoIcon}
        logout={logout}
        finish={finish}
        labelClassName="bg-secondary"
        onToggle={(pinned) => setSidebarPinned(pinned)}
      />
      <div
        className={`flex-1 flex flex-col px-4 sm:px-8 py-6 md:py-7 mt-10 transition-all duration-300 ${
          sidebarPinned ? "ml-[250px]" : "ml-[80px]"
        }`}
      >
        <div className="max-w-6xl mx-auto w-full">
          <span className="text-lg md:text-xl text-[#0A1E4C] font-medium">
            {t("dashboard.greeting", { name: user })}
          </span>
          <h1 className="text-[30px] font-bold text-primary mb-6">
            {t("welcome")} Kikonnect
          </h1>

          <div className="flex items-center gap-3 mb-5">
            <button
              onClick={() => handleMonthChange(-1)}
              className="w-8 h-8 flex items-center justify-center rounded-md border border-gray-200 hover:bg-gray-50"
              aria-label={t("agent.agentDashboard.insights.prevMonth")}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0A1E4C" strokeWidth="2">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <span className="text-base font-medium text-primary capitalize">
              {moment({ year: period.year, month: period.month - 1 }).format("MMMM YYYY")}
            </span>
            <button
              onClick={() => handleMonthChange(1)}
              className="w-8 h-8 flex items-center justify-center rounded-md border border-gray-200 hover:bg-gray-50"
              aria-label={t("agent.agentDashboard.insights.nextMonth")}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0A1E4C" strokeWidth="2">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col justify-center">
              <p className="text-xs text-gray-500 mb-1.5">
                {t("agent.agentDashboard.insights.positionTitle")}
              </p>
              <p className="text-lg font-semibold text-primary mb-2">
                {ranking && ranking.my_position
                  ? t("agent.agentDashboard.insights.positionValue", {
                      position: ranking.my_position,
                      total: ranking.total_agents,
                    })
                  : "\u2014"}
              </p>
              <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#136BF0]"
                  style={{ width: `${rankBarPct}%` }}
                />
              </div>
              <p className="text-[10px] text-gray-400 mt-2">
                {t("agent.agentDashboard.insights.positionScore")}
              </p>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col justify-center">
              <p className="text-xs text-gray-500 mb-1.5">
                {t("agent.agentDashboard.insights.answeredTitle")}
              </p>
              <p className="text-sm mb-0.5">
                <span className="font-semibold">{answeredPct}%</span>{" "}
                <span className="text-gray-400">
                  {t("agent.agentDashboard.insights.answeredYes")}
                </span>
              </p>
              <p className="text-sm mb-2">
                <span className="font-semibold">{notAnsweredPct}%</span>{" "}
                <span className="text-gray-400">
                  {t("agent.agentDashboard.insights.answeredNo")}
                </span>
              </p>
              <div className="w-full h-1.5 rounded-full overflow-hidden flex">
                <div className="h-full bg-[#136BF0]" style={{ width: `${answeredPct}%` }} />
                <div className="h-full bg-gray-300" style={{ width: `${notAnsweredPct}%` }} />
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col">
              <p className="text-xs text-gray-500 mb-2">
                {t("agent.agentDashboard.insights.moodTitle")}
              </p>
              <div className="flex justify-between gap-1">
                {MOOD_OPTIONS.map((m) => (
                  <button
                    key={m.value}
                    onClick={() => handleMoodClick(m.value)}
                    disabled={moodStatus.already_submitted || moodSubmitting}
                    className={`flex-1 flex flex-col items-center justify-center gap-1 py-1.5 rounded-md transition-transform ${
                      moodStatus.already_submitted ? "" : "hover:bg-gray-100 hover:scale-110"
                    }`}
                  >
                    <MoodFace
                      value={m.value}
                      color={moodStatus.already_submitted ? "#9ca3af" : m.color}
                    />
                    <span className="text-[10px] text-gray-400 leading-tight text-center">
                      {t(`agent.agentDashboard.insights.${m.labelKey}`)}
                    </span>
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-gray-400 mt-2">
                {moodStatus.already_submitted
                  ? moodStatus.mood_value <= 2
                    ? t("agent.agentDashboard.insights.moodDoneEmpathy")
                    : t("agent.agentDashboard.insights.moodDonePositive")
                  : t("agent.agentDashboard.insights.moodOncePerDay")}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <p className="text-xs text-gray-500 mb-2">
                {t("agent.agentDashboard.insights.kpiChartTitle")}
              </p>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={kpisByDay}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                  <XAxis dataKey="day" tick={{ fontSize: 9 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 9 }} axisLine={false} tickLine={false} />
                  <Tooltip />
                  <Bar dataKey="realizadas" stackId="a" fill="#136BF0" />
                  <Bar dataKey="contestadas" stackId="a" fill="#1baf7a" />
                  <Bar dataKey="interesados" stackId="a" fill="#eda100" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap gap-3 justify-center mt-2 text-[10px] text-gray-500">
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-sm bg-[#136BF0]" />
                  {t("agent.agentDashboard.insights.legendMade")}
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-sm bg-[#1baf7a]" />
                  {t("agent.agentDashboard.insights.legendAnswered")}
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-sm bg-[#eda100]" />
                  {t("agent.agentDashboard.insights.legendInterested")}
                </span>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <p className="text-xs text-gray-500 mb-2">
                {t("agent.agentDashboard.insights.meetingsChartTitle")}
              </p>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={meetingsByDay}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                  <XAxis dataKey="day" tick={{ fontSize: 9 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 9 }} axisLine={false} tickLine={false} />
                  <Tooltip />
                  <Bar dataKey="agendadas" stackId="b" fill="#136BF0" />
                  <Bar dataKey="efectivas" stackId="b" fill="#1baf7a" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap gap-3 justify-center mt-2 text-[10px] text-gray-500">
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-sm bg-[#136BF0]" />
                  {t("agent.agentDashboard.insights.legendScheduled")}
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-sm bg-[#1baf7a]" />
                  {t("agent.agentDashboard.insights.legendEffective")}
                </span>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              variant="primary"
              className="!w-auto px-6 hover:!bg-[linear-gradient(160deg,#FF9900_0%,#FF6A01_100%)]"
              onClick={handleStartCalls}
              data-tour-id="start-calls-button"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className="mr-2"
              >
                <path d="M15 8a3 3 0 0 1 3 3M15 4a7 7 0 0 1 7 7m-1.5 10.5a17 17 0 0 1-15-15c0-.9.6-1.7 1.4-2l3-1.2a1.6 1.6 0 0 1 2 .8l1.4 3.1a1.6 1.6 0 0 1-.4 1.9l-1.4 1.2a13 13 0 0 0 5.9 5.9l1.2-1.4a1.6 1.6 0 0 1 1.9-.4l3.1 1.4a1.6 1.6 0 0 1 .8 2z" />
              </svg>
              {t("agent.agentDashboard.insights.ctaLabel")}
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className="ml-2"
              >
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </Button>
          </div>
        </div>
      </div>
    </div>
    <div className="lg:hidden">
      <MobileHomeScreen />
    </div>
    </>
  );
};

export default AgentDashboard;
